import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { Link, LinkVariant } from '@jsmdg/yoshi';
import { isBox, isExperience } from '../../../shared/product-safeguards';
import { type Product } from '../../../shared/types';
import { SellOption } from '../../types/enums/sellOption';
import { type ProductDetailContext } from '../../types/types';
import styles from './HowItWorks.module.scss';

type HowItWorksVariant = 'experience' | 'giftbox';

const messages = defineMessages({
    stepEnter: {
        defaultMessage: 'Eingeben',
    },
    stepSelect: {
        defaultMessage: 'Auswählen',
    },
    stepExperience: {
        defaultMessage: 'Erleben',
    },
    enterExperience: {
        defaultMessage:
            'Gutschein kaufen und den Gutscheincode auf {redemptionLink} eingeben oder QR-Code auf dem Gutschein scannen.',
    },
    selectExperience: {
        defaultMessage:
            'Termin & Standort auswählen, um ein Ticket für das Erlebnis des Veranstalters zu erhalten.',
    },
    enterGiftbox: {
        defaultMessage:
            'Gutscheincode auf {redemptionLink} eingeben oder QR-Code auf dem {shareTitle} Gutschein scannen.',
    },
    selectGiftbox: {
        defaultMessage:
            'Alle Erlebnisse dieser Geschenkbox entdecken, Erlebnis, Standort und Veranstalter auswählen und den Gutschein gegen ein Ticket einlösen.',
    },
    experienceStep: {
        defaultMessage:
            'Anschließend mit dem Ticket zum Veranstalter gehen und einen besonderen Moment erleben.',
    },
});

function getHowItWorksVariant(product: Product): HowItWorksVariant | null {
    if (isExperience(product)) {
        return product.sellOption === SellOption.TicketOnly ? null : 'experience';
    }

    if (isBox(product)) {
        return 'giftbox';
    }

    return null;
}

const RedemptionLink = ({ href }: { readonly href: string }): JSX.Element => (
    <Link href={href} rel="noopener noreferrer" target="_blank" variant={LinkVariant.Brand}>
        {href}
    </Link>
);

type HowItWorksProps = {
    readonly variant: HowItWorksVariant;
};

const HowItWorks = ({ variant }: HowItWorksProps): JSX.Element => {
    const intl = useIntl();
    const { tenantConfig } = useFragmentContext<ProductDetailContext>();
    const isGiftbox = variant === 'giftbox';

    const steps = [
        {
            title: intl.formatMessage(messages.stepEnter),
            description: isGiftbox ? messages.enterGiftbox : messages.enterExperience,
        },
        {
            title: intl.formatMessage(messages.stepSelect),
            description: isGiftbox ? messages.selectGiftbox : messages.selectExperience,
        },
        {
            title: intl.formatMessage(messages.stepExperience),
            description: messages.experienceStep,
        },
    ];

    return (
        <ol className="m-0 p-0 pt-2x">
            {steps.map((step, index) => (
                <li
                    key={step.title}
                    className={classNames('d-flex align-items-start gap-1x', {
                        'mb-2x': index < steps.length - 1,
                    })}
                >
                    <span
                        className={classNames(
                            styles.number,
                            'd-flex flex-shrink-0 align-items-center justify-content-center fw-semibold',
                        )}
                        aria-hidden="true"
                    >
                        {index + 1}
                    </span>
                    <div>
                        <div
                            className={classNames(
                                styles.title,
                                'd-flex align-items-center mb-1x fw-semibold',
                            )}
                        >
                            {step.title}
                        </div>
                        <p>
                            <FormattedMessage
                                {...step.description}
                                values={{
                                    redemptionLink: (
                                        <RedemptionLink href={tenantConfig.urls.redemption} />
                                    ),
                                    shareTitle: tenantConfig.shareTitle,
                                }}
                            />
                        </p>
                    </div>
                </li>
            ))}
        </ol>
    );
};

export { getHowItWorksVariant, HowItWorks };
export type { HowItWorksVariant };
